import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Player from 'react-player/vimeo';
import { AnimatePresence, motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PlayIcon } from 'images/shared';

const Video = ({
  title,
  style,
  url,
  customColor,
  autoPlay = false,
  cover = false,
  lazy = true,
  onPlaybackStart
}) => {
  // In autoPlay mode the video plays immediately (muted + looping, no controls);
  // otherwise it stays click-to-play behind the gold play-button overlay.
  // Lazy autoplay (background/cover loops further down a long case study
  // page) start paused instead - the iframe and its poster frame mount
  // straight away so there's no visible pop-in later, but the actual video
  // stream only starts fetching once the player scrolls near the viewport,
  // so mobile connections aren't asked to stream a dozen+ looping videos at
  // once. Hero videos opt out via lazy=false since they're visible
  // immediately and gate the page's entrance overlay.
  const deferPlay = autoPlay && lazy;
  const [playing, setPlaying] = useState(autoPlay && !deferPlay);

  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '400px 0px',
    skip: !deferPlay
  });

  useEffect(() => {
    if (deferPlay && inView) setPlaying(true);
  }, [deferPlay, inView]);

  const handleStart = () => {
    if (onPlaybackStart) onPlaybackStart();
  };

  return (
    <Wrapper ref={ref} style={style} key={url} $cover={cover} $stretch={cover && autoPlay}>
      <Player
        autopause={autoPlay ? undefined : 'true'}
        autopip="true"
        controls={autoPlay ? false : playing}
        // light={!isMobileOnly}
        playing={playing}
        muted={autoPlay}
        loop={autoPlay}
        onStart={autoPlay ? handleStart : undefined}
        onPlay={autoPlay ? handleStart : undefined}
        onPause={() => !autoPlay && setPlaying(false)}
        onEnded={() => !autoPlay && setPlaying(false)}
        playsinline
        stopOnUnmount={false}
        title={title}
        url={url}
        width="100%"
        height="100%"
        aria-hidden="true"
        tabIndex={-1}
        className="video"
        style={{
          position: 'absolute',
          top: 0,
          left: 0
        }}
        config={{
          vimeo: {
            playerOptions: {
              autopause: !autoPlay,
              controls: !autoPlay,
              color: 'CE9200',
              transparent: false,
              background: autoPlay,
              muted: autoPlay,
              loop: autoPlay
            }
          }
        }}
      />
      {!autoPlay && (
        <AnimatePresence>
          {!playing && (
            <Overlay
              onClick={() => setPlaying(true)}
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.5 } }}>
              <Button
                type="button"
                aria-label={`Play Video - ${title}`}
                $customColor={customColor}
                onClick={() => setPlaying(true)}
                initial={{ scale: 1, filter: 'brightness(100%)' }}
                whileHover={{ scale: 1.075, filter: 'brightness(107.5%)' }}
                whileTap={{ scale: 0.95 }}>
                <Shine />
                <StyledPlayIcon />
              </Button>
            </Overlay>
          )}
        </AnimatePresence>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  /* Stretch mode (autoplay, no controls): the Vimeo iframe always letterboxes
     its own 16:9 content internally, so simply setting width/height: 100% on
     an odd-shaped box just moves the letterboxing into our box instead of
     removing it. To get a true crop we keep the player itself locked at
     16:9, oversize it with min-width/min-height: 100% so it's guaranteed to
     be at least as big as the cell in both directions, then let the outer
     overflow: hidden clip the excess. Safe here since there's no control bar
     to protect.
     Cover mode (controls visible): keep the player at 16:9 and match the
     parent cell's full height (centred horizontally). The Vimeo player's own
     controls sit at the true bottom edge of the frame, so we only ever crop
     width (never height) - otherwise the control bar gets clipped by the
     cell's overflow: hidden.
     Default keeps the 16:9 aspect ratio via padding. */
  ${({ $cover, $stretch }) => {
    if ($stretch) {
      return `
        width: 100%;
        height: 100%;
        overflow: hidden;

        .video {
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%);
          width: auto !important;
          height: auto !important;
          min-width: 100% !important;
          min-height: 100% !important;
          aspect-ratio: 16 / 9;
        }
        .video iframe {
          width: 100% !important;
          height: 100% !important;
        }
      `;
    }
    if ($cover) {
      return `
        width: 100%;
        height: 100%;
        overflow: hidden;

        .video {
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%);
          height: 100% !important;
          width: auto !important;
          min-width: 100% !important;
          aspect-ratio: 16 / 9;
        }
        .video iframe {
          width: 100% !important;
          height: 100% !important;
        }
      `;
    }
    return 'padding: 56.25% 0 0 0;';
  }}
  ${({ style }) => style};
`;

const Overlay = styled(motion.div)`
  align-items: center;
  background-color: rgba(000, 000, 000, 0.15);
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const Button = styled(motion.button)`
  align-items: center;
  background-color: ${({ $customColor }) => $customColor ? `${$customColor}` : '#ce9200'};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: 40px;
  height: 40px;

  @media screen and (min-width: 550px) {
    width: 100px;
    height: 100px;
  }
`;

const StyledPlayIcon = styled(PlayIcon)`
  cursor: pointer;
  margin-left: 12%;
  width: 39%;
`;

const Shine = styled.div`
  animation: slide 1.75s infinite;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(128, 186, 232, 0) 99%,
    rgba(125, 185, 232, 0) 100%
  );
  content: '';
  height: 100%;
  position: absolute;
  top: 0;
  transform: translateX(100%);
  width: 100%;
  z-index: -1;

  @keyframes slide {
    0% {
      transform: translateX(-100%);
    }
    50% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

export default Video;
